import {
  Box,
  Column,
  Row,
  Skeleton,
  SkeletonBox,
  Text,
  Tooltip,
} from "@hightouch/htui";
import type {
  MetricComparison,
  MetricDelta,
  MetricRowState,
} from "src/pages/ad-studio/insights/performance/view/types";

type MetricRowsProps = { metrics: readonly MetricRowState[] };

export const MetricRows = ({ metrics }: MetricRowsProps) => (
  <Column gap={2}>
    {metrics.map((metric) => (
      <MetricRow key={metric.key} metric={metric} />
    ))}
  </Column>
);

const MetricRow = ({ metric }: { metric: MetricRowState }) => (
  <Row justify="space-between" align="center" gap={2}>
    <MetricLabel metric={metric} />
    <MetricValue value={metric.value} comparison={metric.comparison} />
  </Row>
);

const MetricValue = ({
  value,
  comparison,
}: {
  value: string;
  comparison: MetricComparison;
}) => {
  switch (comparison.kind) {
    case "average-pending": {
      return (
        <Skeleton isLoading>
          <SkeletonBox height={5} width={14} borderRadius="md" />
        </Skeleton>
      );
    }
    case "compared": {
      return (
        <Tooltip
          isDisabled={comparison.delta.kind === "no-value"}
          message={<DeltaTooltip delta={comparison.delta} />}
        >
          <Box
            px={2}
            py={0.5}
            borderRadius="md"
            bg={comparison.tone.bg}
            color={comparison.tone.color}
            minWidth={14}
            textAlign="right"
          >
            <Text as="span" variant="monospace" size="sm" color="inherit">
              {value}
            </Text>
          </Box>
        </Tooltip>
      );
    }
    default: {
      return comparison satisfies never;
    }
  }
};

const MetricLabel = ({ metric }: { metric: MetricRowState }) => {
  switch (metric.description.kind) {
    case "no-description": {
      return (
        <Text size="sm" color="text.secondary">
          {metric.label}
        </Text>
      );
    }
    case "shown": {
      return (
        <Tooltip message={metric.description.text}>
          <Box
            display="inline-block"
            color="text.secondary"
            textDecoration="underline"
            textDecorationStyle="dotted"
            sx={{
              textUnderlineOffset: "var(--chakra-space-0-5)",
              textDecorationSkipInk: "none",
            }}
          >
            <Text as="span" size="sm" color="inherit">
              {metric.label}
            </Text>
          </Box>
        </Tooltip>
      );
    }
    default: {
      return metric.description satisfies never;
    }
  }
};

const DeltaTooltip = ({ delta }: { delta: MetricDelta }) => {
  switch (delta.kind) {
    case "versus-average": {
      return (
        <Row as="span" gap={1} align="center">
          <Text
            as="span"
            size="sm"
            color={delta.tone === "better" ? "green.200" : "red.200"}
          >
            {delta.text}
          </Text>
          <Text as="span" size="sm" color="inherit">
            vs average of all ads
          </Text>
        </Row>
      );
    }
    case "no-average":
    case "no-value": {
      return <>No average available</>;
    }
    default: {
      return delta satisfies never;
    }
  }
};
